/**
 * Role Master · Aug 31, 2026
 *
 * Dedicated admin screen for organizational role CRUD. Sits alongside
 * User Master (`/masters/users`) and the Permissions Matrix
 * (`/settings/roles`) — this trio is the "User Security" section, all
 * three gated to Administrator via `adminOnly` in the sidebar.
 *
 * Contract:
 *   - Admin adds / edits / deletes roles here; permissions are NOT
 *     assigned on this screen (see Permissions Matrix).
 *   - The Administrator role is protected — cannot be edited or
 *     deleted from the UI.
 *   - Every role created here appears in the User Master role picker
 *     and the Permissions Matrix role selector immediately (both
 *     pull from `GET /rbac/roles`).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function RoleMaster() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [form, setForm] = useState({ code: "", name: "", description: "" });
  // Orphan reassignment state (Aug 31, 2026)
  const [orphansOpen, setOrphansOpen] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [orphanSelection, setOrphanSelection] = useState({}); // {user_id: true}
  const [orphanTargetRole, setOrphanTargetRole] = useState("");
  const [orphansBusy, setOrphansBusy] = useState(false);

  const isAdmin = user?.role === "Administrator";

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/rbac/roles");
      setRoles(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load roles");
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  // Aug 31, 2026 · Fetch orphans whenever the dialog is opened so admins
  // see the current state of the system after any manual edits.
  useEffect(() => {
    if (!orphansOpen) return;
    (async () => {
      try {
        const r = await api.get("/users/orphans");
        setOrphans(r.data?.orphans || []);
        setOrphanSelection({});
        // Default target role = first non-Administrator role, else Administrator.
        const firstUsable = (roles.find(x => x.code !== "Administrator") || roles[0])?.code || "";
        setOrphanTargetRole(firstUsable);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load orphan users");
      }
    })();
  }, [orphansOpen, roles]);

  const groupedOrphans = orphans.reduce((acc, u) => {
    (acc[u.role || "—"] = acc[u.role || "—"] || []).push(u); return acc;
  }, {});

  const toggleOrphan = (id) => setOrphanSelection(s => ({ ...s, [id]: !s[id] }));
  const selectAllOrphans = () => setOrphanSelection(Object.fromEntries(orphans.map(o => [o.id, true])));
  const clearOrphanSelection = () => setOrphanSelection({});

  const applyReassign = async () => {
    const ids = Object.entries(orphanSelection).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return toast.error("Select at least one user");
    if (!orphanTargetRole) return toast.error("Choose a target role");
    setOrphansBusy(true);
    try {
      const r = await api.post("/users/bulk-reassign", { user_ids: ids, target_role: orphanTargetRole });
      toast.success(`Reassigned ${r.data?.modified ?? ids.length} user(s) to ${orphanTargetRole}`);
      setOrphansOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    }
    setOrphansBusy(false);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ code: "", name: "", description: "" });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    if (r.code === "Administrator") return toast.error("Administrator is a protected role");
    setEditing(r);
    setForm({ code: r.code, name: r.name || "", description: r.description || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    const code = (form.code || "").trim();
    const name = (form.name || "").trim();
    if (!code || !name) return toast.error("Code and Name are required");
    try {
      if (editing) {
        await api.put(`/rbac/roles/${encodeURIComponent(editing.code)}`, {
          code, name, description: form.description || "",
        });
        toast.success(`Role ${code} updated`);
      } else {
        await api.post("/rbac/roles", { code, name, description: form.description || "" });
        toast.success(`Role ${code} created — configure permissions on the Permissions Matrix.`);
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (r) => {
    if (r.code === "Administrator") return toast.error("Administrator is a protected role");
    if (!window.confirm(`Delete role "${r.code}"? All permission grants will be revoked and any user assigned this role will lose access until reassigned.`)) return;
    try {
      await api.delete(`/rbac/roles/${encodeURIComponent(r.code)}`);
      toast.success(`Role ${r.code} deleted`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!isAdmin) {
    return (
      <div data-testid="role-master-locked" className="p-6 text-sm text-zinc-400">
        <div className="text-lg text-rose-400 mb-1">Access Restricted</div>
        Role Master is available to the Administrator role only.
      </div>
    );
  }

  return (
    <div data-testid="role-master-page" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-lg text-white">Role Master</div>
          <div className="text-xs text-zinc-500">
            Manage organizational roles. Assign permissions on the
            <a href="/settings/roles" className="underline text-sky-400 ml-1">Permissions Matrix</a>.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="role-master-orphans-btn"
            variant="outline"
            onClick={() => setOrphansOpen(true)}
            className="border-amber-500/50 text-amber-300 hover:bg-amber-950"
          >
            Reassign Orphans
          </Button>
          <Button
            data-testid="role-master-new-btn"
            onClick={openNew}
            className="bg-sky-600 hover:bg-sky-500 text-white"
          >
            + New Role
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-right px-3 py-2 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-500">Loading…</td></tr>
            )}
            {!loading && roles.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-500">No roles yet — click <b>New Role</b> to add one.</td></tr>
            )}
            {!loading && roles.map(r => {
              const locked = r.code === "Administrator";
              return (
                <tr
                  key={r.code}
                  data-testid={`role-row-${r.code}`}
                  className="border-t border-zinc-800 hover:bg-zinc-900/60"
                >
                  <td className="px-3 py-2 font-mono text-sky-300">{r.code}</td>
                  <td className="px-3 py-2 text-white">{r.name}</td>
                  <td className="px-3 py-2 text-zinc-400">{r.description || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <RowActions
                      testid={`role-actions-${r.code}`}
                      items={[
                        { label: "Edit", testid: `role-edit-${r.code}`, disabled: locked, onClick: () => openEdit(r) },
                        { label: "Delete", tone: "danger", testid: `role-delete-${r.code}`, disabled: locked, onClick: () => remove(r) },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="role-master-dialog" className="bg-zinc-950 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Role · ${editing.code}` : "New Role"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="role-code">Code</Label>
              <Input
                id="role-code"
                data-testid="role-master-code"
                value={form.code}
                disabled={!!editing}
                onChange={e => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. Manager"
              />
              {editing && <div className="text-xs text-zinc-500 mt-1">Code is immutable after creation.</div>}
            </div>
            <div>
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name"
                data-testid="role-master-name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Manager"
              />
            </div>
            <div>
              <Label htmlFor="role-desc">Description</Label>
              <Textarea
                id="role-desc"
                data-testid="role-master-desc"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Optional — where this role sits in the org chart"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="role-master-cancel">Cancel</Button>
            <Button onClick={save} data-testid="role-master-save" className="bg-sky-600 hover:bg-sky-500">
              {editing ? "Save changes" : "Create role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aug 31, 2026 · Orphaned Users Reassignment ────────────────── */}
      <Dialog open={orphansOpen} onOpenChange={setOrphansOpen}>
        <DialogContent data-testid="orphans-dialog" className="bg-zinc-950 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reassign Orphaned Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-zinc-400">
              These users hold a role that no longer exists in Role Master — they get 403 everywhere until reassigned.
              Pick a target role below, tick the users you want to migrate, then Apply.
            </div>
            {orphans.length === 0 ? (
              <div data-testid="no-orphans" className="p-6 text-center text-zinc-500 border border-zinc-800 rounded-md">
                No orphaned users. Everyone's role is valid.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Label htmlFor="orphan-target" className="text-xs text-zinc-400">Target Role:</Label>
                  <select
                    id="orphan-target"
                    data-testid="orphan-target-role"
                    value={orphanTargetRole}
                    onChange={e => setOrphanTargetRole(e.target.value)}
                    className="h-8 px-2 bg-zinc-900 border border-zinc-700 rounded-sm text-sm text-white"
                  >
                    {roles.map(r => (
                      <option key={r.code} value={r.code}>{r.code}</option>
                    ))}
                  </select>
                  <div className="flex-1" />
                  <button
                    data-testid="orphans-select-all"
                    onClick={selectAllOrphans}
                    className="text-xs px-2 py-1 border border-zinc-700 rounded text-zinc-300 hover:bg-zinc-900"
                  >Select all</button>
                  <button
                    data-testid="orphans-clear"
                    onClick={clearOrphanSelection}
                    className="text-xs px-2 py-1 border border-zinc-700 rounded text-zinc-300 hover:bg-zinc-900"
                  >Clear</button>
                </div>
                <div className="max-h-80 overflow-y-auto border border-zinc-800 rounded-md divide-y divide-zinc-800">
                  {Object.entries(groupedOrphans).map(([oldRole, list]) => (
                    <div key={oldRole} className="p-2">
                      <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">
                        {oldRole} · {list.length} user{list.length === 1 ? "" : "s"}
                      </div>
                      {list.map(o => (
                        <label
                          key={o.id}
                          data-testid={`orphan-row-${o.id}`}
                          className="flex items-center gap-2 py-1 text-sm text-white cursor-pointer hover:bg-zinc-900 px-2 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={!!orphanSelection[o.id]}
                            onChange={() => toggleOrphan(o.id)}
                          />
                          <span className="font-mono text-xs text-zinc-300">{o.email}</span>
                          <span className="text-zinc-500 text-xs">· {o.name || "—"}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOrphansOpen(false)} data-testid="orphans-cancel">Cancel</Button>
            <Button
              onClick={applyReassign}
              disabled={orphansBusy || orphans.length === 0}
              data-testid="orphans-apply"
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              {orphansBusy ? "Applying…" : "Apply Reassignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
